// Styles
import { useWidget } from '@telescope/cassini-hooks';
import { AspectRatio, Box, Button, Flex, Heading, Image, ScaleFade, Hide, Text } from '@telescope/cassini-ui';
import { getAppConfig } from '@telescope/cassini-utilities';
import { Open, Winner } from 'generated-types/widget';
import { ReactElement, useMemo, useRef } from 'react';
import { VoteOption } from 'types';
import { BracketSize, useBracketGrid } from '../providers/BracketGridProvider';
import { stylesheet } from './BracketGridVoteOption.styles';
import { VoteButtonProps } from './VoteButton';

const { isPreviewUrl } = getAppConfig();

// Depending on the bracket size, the region label will be in a different position based on the matchup number.
// This map is used to determine the region label position based on the matchup number.
const regionPositionMap: Record<BracketSize, Record<number, string>> = {
  32: {
    1: 'topLeft',
    5: 'bottomLeft',
    9: 'topRight',
    13: 'bottomRight',
  },
  16: {
    1: 'topLeft',
    3: 'bottomLeft',
    5: 'topRight',
    7: 'bottomRight',
  },
  8: {
    1: 'topLeft',
    2: 'bottomLeft',
    3: 'topRight',
    4: 'bottomRight',
  },
};

const getRegionTitle = (
  quadrantTitles: Record<string, string>,
  round: number,
  bracketSize: BracketSize,
  matchup: number,
  position: number
) => {
  if (round !== 1 || position !== 0) return;

  const regionPosition = regionPositionMap[bracketSize][matchup];
  return quadrantTitles[regionPosition];
};

interface BracketGridVoteOptionProps {
  index: number;
  voteOption: VoteOption;
  columns: number;
  roundDisplay: number;
  isActive: boolean;
  isVisible: boolean;
  isWinner: boolean;
  voteButton?: ReactElement<VoteButtonProps>;
  onViewDetail(option: VoteOption): void;
}

export const BracketGridVoteOption = ({
  index,
  voteOption,
  columns,
  roundDisplay,
  isActive,
  isVisible,
  isWinner,
  voteButton,
  onViewDetail,
}: BracketGridVoteOptionProps) => {
  const { data } = useWidget({ select: (data: Open | Winner) => data.widget.globalViews.bracketViewGrid });
  const { content, styles } = data!;
  const { isVoteOptionSelected, activeRound, totalContestants } = useBracketGrid();
  const { roundId, region, position, positionInMatchup } = voteOption;
  const isSelected = isActive && isVoteOptionSelected(voteOption);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const style = stylesheet({
    isWinner,
    round: roundId,
    region,
    position,
    columns,
    roundDisplay,
    isActive,
    height: imageRef.current?.height,
  });
  const shouldDisplayVoteButton = (isPreviewUrl && voteOption.roundId === activeRound) || isActive; // Force the vote button to display in preview mode

  const regionTitle = useMemo(
    () => getRegionTitle(content.quadrantTitles, roundId, totalContestants, voteOption.match_up_id, positionInMatchup),
    [content.quadrantTitles, roundId, totalContestants, voteOption.match_up_id, positionInMatchup]
  );

  return (
    <Box
      className="BracketGridVoteOption"
      sx={style.voteOption}
      width="100%"
      role="option"
      aria-selected={isSelected}
      data-vote-option-name={voteOption.name}
      data-matchup={voteOption.match_up_id}
      position="relative">
      <ScaleFade initialScale={0} in={isVisible} unmountOnExit={true}>
        <Box width="100%" justifyContent="flex-start" pos="absolute" transform="translateY(-100%)">
          <Hide above="lg">
            {regionTitle && (
              <Flex className="BracketGridVoteOption__region-title" justify="flex-start" width="100%" mb={4}>
                <Heading size="md" as="h2" whiteSpace="nowrap" marginBlock={0} m={0}>
                  {regionTitle}
                </Heading>
              </Flex>
            )}
          </Hide>
          {isWinner && (
            <Text
              className="BracketGridVoteOption__winner-label"
              textAlign="center"
              fontSize="xs"
              fontWeight={600}
              mb={2}
              {...styles.winnerLabel}>
              {content.winnerLabel}
            </Text>
          )}
          <Text className="BracketGridVoteOption__label" sx={style.name} color="BracketGridVoteOption-label">
            {voteOption.displayName}
          </Text>
        </Box>

        <Button
          className="BracketGridVoteOption__thumbnail-btn"
          isDisabled={!voteOption.referenceId && !isWinner}
          _disabled={{ opacity: 1, boxShadow: 'none', pointerEvents: 'none' }}
          onClick={() => onViewDetail(voteOption)}
          variant="unstyled"
          rounded={0}
          h="unset"
          width="100%"
          border={isWinner ? `2px solid var(--cassini-colors-primary-500) ` : 'none'}>
          {voteOption.statistics && (
            <Text as="span" pos="absolute" bottom={1.5} right={1.5} fontSize={{ base: '3.5vw', md: '2vw', lg: 'md' }}>
              {voteOption.statistics.percentage}%
            </Text>
          )}
          <AspectRatio ratio={16 / 9}>
            <Image
              className="BracketGridVoteOption__thumbnail-img"
              src={voteOption.image}
              fallbackSrc={
                content.placeholderImage ??
                'https://ts-cms-production.votenow.tv/campaign/10/05/1005594/166026158062f594cc76f661.02211738.jpg'
              }
              alt={voteOption.name}
              ref={imageRef}
            />
          </AspectRatio>
        </Button>
        {shouldDisplayVoteButton && <>{voteButton}</>}
      </ScaleFade>
    </Box>
  );
};
